'use strict';

import {BaseElement} from "../../../../interfaces/BaseElement";
import {Buildings, components, isDesktop, projectMode, settingsData} from "../../../../../settings";
import {
    anyApartmentsInSearch,
    areAllApartmentsSold,
    findSelectionAndCrmData,
    rotation_to_flat,
    userPrevent
} from "../../../../../functions";
import { getCurrentLang} from "../../../../components/langSwitcher/langSwitcher";
import {ResultsAmount} from "./ResultsAmount";
import {EventBus} from "../../../../interfaces/EventBus";
import {SwipeScript} from "../../../../controls/swipe";
import { renderUpdateValuesOnDemand } from '../../../../../app';
import {hoverOutPreviousSelection} from "../../../../threejs/raycast";

export class BuildingSwitcher {
    static #buildings = [];
    #rootElement;
    #buildingName;
    #leftArrow;
    #rightArrow;
    #resultsAmount;

    constructor() {
        this.#rootElement = new BaseElement()
            .addClass(
                'mobile-search__building-switcher',
                Buildings.getBuildingsInfoLength() < 2 ? 'mobile-search__building-switcher--one-building' : null,
                projectMode.LiteMode.modeIs('picture') || projectMode.LiteMode.modeIs('empty') || config.features?.privateHouseMode
                    ? 'mobile-search__building-switcher--lite-picture' : null
            );

        this.#createArrayOfBuildings();
        this.#build();
        this.#addSwipeLogic();

        EventBus.add('buildingSwitcherSetTitle', this.#setTitle.bind(this));
        EventBus.add('buildingSwitcherSetActiveBuildingName', this.setActiveBuildingName.bind(this));
        EventBus.add('buildingSwitcherSetArrowsVisibilityTo', this.#setArrowsVisibilityTo.bind(this));
        EventBus.add('buildingSwitcherShow', this.#show.bind(this));
        EventBus.add('buildingSwitcherHide', this.#hide.bind(this));
    }

    static get listOfBuildings() {
        return BuildingSwitcher.#buildings;
    }

    get rootElement() {
        return this.#rootElement;
    }

    remove() {
        EventBus.remove('buildingSwitcherSetTitle');
        EventBus.remove('buildingSwitcherSetActiveBuildingName');
        EventBus.remove('buildingSwitcherSetArrowsVisibilityTo');
        EventBus.remove('buildingSwitcherShow');
        EventBus.remove('buildingSwitcherHide');

        this.#resultsAmount.remove();
        this.#resultsAmount = null;

        this.#rootElement.remove();
        this.#rootElement = null;
    }

    setActiveBuildingName() {
        const activeBuilding = BuildingSwitcher.#buildings.find(item => item.name === Buildings.getActiveBuilding().name);
        activeBuilding.towerNames.forEach(name => {
            this.#buildingName.addAttribute(`data-lang-${name.lang}`, name.value);
            if (name.lang === getCurrentLang())
                this.#buildingName.innerHtml(name.value)
        });
    }

    #setTitle(type) {
        switch (type) {
            case 'apartments':
                this.#buildingName.removeClass('hidden');
                this.#resultsAmount.rootElement.removeClass('hidden');
                break;
        }
    }

    #build() {
        const infoElement = new BaseElement()
            .addClass('building-switcher__info-element')
            .appendTo(this.#rootElement.DOMElement);

        this.#buildingName = new BaseElement('span')
            .addClass('building-switcher__building-name', 'language-string')
            .appendTo(infoElement.DOMElement);

        this.#resultsAmount = new ResultsAmount();
        this.#resultsAmount.rootElement.appendTo(infoElement.DOMElement);

        this.#leftArrow = new BaseElement('button')
            .addClass('building-switcher__arrow', 'building-switcher__arrow--left', 'icon-left1')
            .addAttribute('data-switch-direction', 'prev')
            .event('click', this.#switchHandler.bind(this))
            .appendTo(this.#rootElement.DOMElement);

        this.#rightArrow = new BaseElement('button')
            .addClass('building-switcher__arrow', 'building-switcher__arrow--right', 'icon-right1')
            .addAttribute('data-switch-direction', 'next')
            .event('click', this.#switchHandler.bind(this))
            .appendTo(this.#rootElement.DOMElement);
    }

    #createArrayOfBuildings() {
        Buildings.getBuildingsArray().forEach(building => {
            if (building.info && building.getInfo().crm_data) {
                const towerNames = building.info.towerName.map(item => {
                    const {lang, value} = item;

                    if (value.length <= 3) {
                        item.value = `${c.dictionary['building'][lang]} ${value}`;
                    }

                    return item;
                });

                BuildingSwitcher.#buildings.push({name: building.name, towerNames});
            }
        });
    }

    #switchHandler(event) {
        if (userPrevent() || settingsData.activeModeSwitching || settingsData.buildingDestroyed)
            return;

        settingsData.activeModeSwitching = true;
        let switchDirection;
        switch(event.type) {
            case 'click':
                switchDirection = event.target.getAttribute('data-switch-direction');
                break;
            case 'swiped-right':
                switchDirection = 'next';
                break;
            case 'swiped-left':
                switchDirection = 'prev';
                break;
        }

        const buildingName = this.#getBuildingNameToSwitch(switchDirection);
        renderUpdateValuesOnDemand(false);
        hoverOutPreviousSelection();
        projectMode.Cylinder.switchBuilding(buildingName)
            .then(() => {
                this.setActiveBuildingName();
                // EventBus.trigger('resultsViewerToggleContent')
                EventBus.trigger('resultsViewerSlideToApartment');
                const {selectedMesh} = findSelectionAndCrmData(components.mobileSearch.getApartmentId());
                rotation_to_flat(selectedMesh, true)
            });
    }

    #addSwipeLogic() {
        SwipeScript(this.#rootElement.DOMElement);
            this.#rootElement.event('swiped-right', this.#switchHandler.bind(this));
            this.#rootElement.event('swiped-left', this.#switchHandler.bind(this));
    }

    #getBuildingNameToSwitch(direction) {
        const activeBuildingName = Buildings.getActiveBuilding().name;
        let index = BuildingSwitcher.#buildings.findIndex(element => element.name === activeBuildingName);
        let amountOfSwitches = 0;
        switch (direction) {
            case 'next':
                do {
                    amountOfSwitches++;
                    if (amountOfSwitches > BuildingSwitcher.#buildings.length)
                        return index;
                    index = (index + 1) % BuildingSwitcher.#buildings.length;
                } while (areAllApartmentsSold(Buildings.getBuilding(BuildingSwitcher.#buildings[index].name)) ||
                         anyApartmentsInSearch(Buildings.getBuilding(BuildingSwitcher.#buildings[index].name)))


                break;
            case 'prev':
                do {
                    amountOfSwitches++;
                    if (amountOfSwitches > BuildingSwitcher.#buildings.length)
                        return index;
                    index = (index - 1 + BuildingSwitcher.#buildings.length) % BuildingSwitcher.#buildings.length;
                } while (areAllApartmentsSold(Buildings.getBuilding(BuildingSwitcher.#buildings[index].name)) ||
                         anyApartmentsInSearch(Buildings.getBuilding(BuildingSwitcher.#buildings[index].name)))

                break;
        }

        return BuildingSwitcher.#buildings[index].name;
    }

    #setArrowsVisibilityTo(action) {
        if (action === 'hidden') {
            this.#leftArrow.addClass('hidden');
            this.#rightArrow.addClass('hidden');
        } else if (action === 'visible') {
            this.#leftArrow.removeClass('hidden');
            this.#rightArrow.removeClass('hidden');
        }

    }

    #hide() {
        this.#rootElement.addClass('hidden');
    }

    #show() {
        this.#rootElement.removeClass('hidden');
    }
}
